import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import Permissions from '../../auth/Permissions';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import WarningHeader from '../../common/text/WarningHeader';
import { getProjectMaterials, updateProjectMaterials } from '../../materials/state/materials.actions';
import MaterialTableAddDrawer from '../../forms/FormBuilder/FormFields/MaterialTableAddDrawer';
import ProjectMaterialsList from './ProjectMaterialsList';
import ProjectMaterialDrawer from './ProjectMaterialDrawer';

const removeModal = (title, onOk) => CustomConfirmModal({
  title: (
    <WarningHeader
      justify="center"
      title={title}
    />
  ),
  onOk,
});

export default function ProjectAddMaterials({
  divisionId,
  projectId,
}) {
  const dispatch = useDispatch();
  const [showNew, setShowNew] = useState(false);
  const [showExisting, setShowExisting] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState();

  const canEdit = Permissions.has('PROJECTS_WRITE');

  const projectMaterials = useSelector((state) => state.materials.projectMaterials[projectId]);
  const globalMaterials = useSelector((state) => state.materials.materials);

  const existingGlobalMaterialIds = useMemo(() => {
    const ids = new Set();
    projectMaterials?.forEach((material) => {
      if (material?.globalMaterialId) {
        ids.add(material.globalMaterialId);
      }
    });
    return ids;
  }, [projectMaterials]);

  const availableGlobalMaterials = useMemo(() => {
    const map = {};
    Object.values(globalMaterials ?? {}).forEach((material) => {
      if (
        material.active
        && !material.isFolder
        && !material.isAssembly
        && !existingGlobalMaterialIds.has(material.id)
        && material.divisionId === divisionId
      ) {
        map[material.id] = material;
      }
    });
    return map;
  }, [globalMaterials, existingGlobalMaterialIds, divisionId]);

  useEffect(() => {
    if (!projectId) return;
    dispatch(getProjectMaterials(projectId));
  }, [projectId]);

  const saveMaterials = useCallback(async (payload) => {
    if (!projectId) return false;
    const success = await dispatch(updateProjectMaterials(projectId, payload));
    if (success) return true;
    message.error('Could not update project materials.');
    return false;
  }, [projectId]);

  const onAddNew = useCallback(() => {
    setSelectedMaterial();
    setShowNew(true);
  }, []);

  const onAddExistingClick = useCallback(() => setShowExisting(true), []);
  const onHideExisting = useCallback(() => setShowExisting(false), []);

  const closeDrawer = useCallback(() => {
    setShowNew(false);
    setSelectedMaterial();
  }, []);

  const onSubmitNew = useCallback(async (values) => {
    if (!projectId) return;

    if (selectedMaterial) {
      const success = await saveMaterials({
        modifiedMaterials: [{
          id: selectedMaterial.id,
          ...values,
        }],
      });
      if (success) closeDrawer();
      return;
    }

    const success = await saveMaterials({
      createdMaterials: [values],
    });
    if (success) closeDrawer();
  }, [projectId, selectedMaterial, saveMaterials, closeDrawer]);

  const onSubmitExisting = useCallback(async (selected = []) => {
    if (!projectId || !selected.length) return;

    const selectedMaterials = selected
      .map((id) => availableGlobalMaterials[id])
      .filter(Boolean);
    if (!selectedMaterials.length) return;

    const success = await saveMaterials({
      fromGlobalMaterials: selectedMaterials.map((material) => ({
        id: material.id,
        name: material.name,
        description: material.description,
        code: material.code,
        partNumber: material.partNumber,
        supplier: material.supplier,
        vendorId: material.vendorId,
        units: material.units,
        cost: material.cost,
        markup: material.markup,
        labourCost: material.labourCost,
        labourMarkup: material.labourMarkup,
        costcodeId: material.costcodeId,
        // Project materials are single division: they belong to the project's division,
        // not to whichever divisions the global material happens to span.
        divisionId,
      })),
    });
    if (success) setShowExisting(false);
  }, [projectId, divisionId, saveMaterials, availableGlobalMaterials]);

  const removeMaterials = useCallback((records, onCleared) => {
    if (!projectId || !canEdit || !records?.length) return;

    const title = records.length === 1
      ? `Remove material “${records[0].name || 'this material'}” from this project?`
      : `Remove ${records.length} materials from this project?`;

    removeModal(title, async () => {
      const success = await saveMaterials({
        removedMaterials: records.map((record) => record.id),
      });
      if (success) {
        onCleared?.();
      }
    });
  }, [projectId, canEdit, saveMaterials]);

  const onDeleteSelected = useCallback((records, onCleared) => {
    removeMaterials(records, onCleared);
  }, [removeMaterials]);

  const onDeleteFromDrawer = useCallback(() => {
    if (!selectedMaterial) return;
    removeMaterials([selectedMaterial], closeDrawer);
  }, [selectedMaterial, removeMaterials, closeDrawer]);

  const onRowClick = useCallback((record) => {
    setSelectedMaterial(record);
    setShowNew(true);
  }, []);

  return (
    <div className="project-add-costcode-container" style={{ paddingLeft: 5, paddingTop: 5 }}>
      <ProjectMaterialsList
        title="Materials"
        canEdit={canEdit}
        onAddNew={onAddNew}
        onAddExisting={onAddExistingClick}
        onDeleteSelected={onDeleteSelected}
        chosen={projectMaterials}
        onRowClick={onRowClick}
      />
      <ProjectMaterialDrawer
        visible={showNew}
        onClose={closeDrawer}
        onSubmit={onSubmitNew}
        onDelete={onDeleteFromDrawer}
        item={selectedMaterial}
        divisionId={divisionId}
        projectId={projectId}
        isNotDisplay={canEdit}
      />
      <MaterialTableAddDrawer
        visible={showExisting}
        onClose={onHideExisting}
        onSubmit={onSubmitExisting}
        materials={availableGlobalMaterials}
        title="Add Existing Materials"
      />
    </div>
  );
}

ProjectAddMaterials.propTypes = {
  divisionId: PropTypes.string,
  projectId: PropTypes.string,
};

ProjectAddMaterials.defaultProps = {
  divisionId: undefined,
  projectId: undefined,
};
